import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';
import api from '../../../services/api';
import PostCard from '../../../components/PostCard';
import MetaTags from '../../../components/MetaTags';
import { Post } from '../../../types';
import './Feed.css';

// ==================== TYPES ====================
interface FeedCache {
  posts: Post[];
  page: number;
  hasMore: boolean;
  timestamp: number;
}

// ==================== CONSTANTS ====================
const CACHE_KEY = 'foryouFeedCache';
const POSTS_PER_PAGE = 10;

// ==================== HELPER FUNCTIONS ====================
const getInitialCache = (): FeedCache => {
  try {
    const savedCache = sessionStorage.getItem(CACHE_KEY);
    if (savedCache) {
      const parsed = JSON.parse(savedCache);
      return parsed;
    }
  } catch (e) {
    // Cache parse error handled silently
  }

  return {
    posts: [],
    page: 1,
    hasMore: true,
    timestamp: 0
  };
};


// ==================== MAIN COMPONENT ====================
const ForYou: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ==================== STATE ====================
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [feedCache, setFeedCache] = useState<FeedCache>(getInitialCache);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [newPostsLoaded, setNewPostsLoaded] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // ==================== REFS ====================
  const feedPostsListRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  const fetchInProgressRef = useRef(false);

  // ==================== CACHE MANAGEMENT ====================
  // Persist cache to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(feedCache));
    } catch (e) {
      // Cache save error handled silently
    }
  }, [feedCache]);

  // Update cache
  const updateCache = useCallback((updates: Partial<FeedCache>) => {
    setFeedCache(prev => ({
      ...prev,
      ...updates,
      timestamp: Date.now()
    }));
  }, []);

  // ==================== API FUNCTIONS ====================
  const fetchFeed = useCallback(async (
    pageNum: number = 1,
    append: boolean = false,
    forceRefresh: boolean = false
  ): Promise<void> => {
    // Prevent duplicate requests
    if (fetchInProgressRef.current) {
      return;
    }

    // Set loading states
    if (pageNum === 1 && !append) {
      setLoading(true);
    } else if (pageNum > 1) {
      setLoadingMore(true);
    }

    fetchInProgressRef.current = true;
    setError(null);

    try {
      const refreshParam = forceRefresh ? '&refresh=true' : '';
      const response = await api.get(`/posts/foryou?page=${pageNum}&limit=${POSTS_PER_PAGE}${refreshParam}`);

      if (!isMountedRef.current) return;

      const { posts: newPosts, pagination } = response.data;

      if (append && pageNum > 1) {
        // Append new posts
        setPosts(prevPosts => {
          const existingIds = new Set(prevPosts.map(post => post.id));
          const uniqueNewPosts = newPosts.filter((post: Post) => !existingIds.has(post.id));
          const updatedPosts = [...prevPosts, ...uniqueNewPosts];

          // Update cache with appended posts
          updateCache({
            posts: updatedPosts,
            page: pageNum,
            hasMore: pagination.hasMore
          });

          // Track new post IDs for animation (only if not already animating)
          if (uniqueNewPosts.length > 0 && newPostsLoaded.length === 0) {
            const newPostIds = uniqueNewPosts.map((post: Post) => post.id);
            setNewPostsLoaded(newPostIds);
            setTimeout(() => setNewPostsLoaded([]), 600);
          }

          return updatedPosts;
        });
      } else {
        // Replace posts (fresh load) - ensure no duplicates
        const uniquePosts = newPosts.filter((post: Post, index: number, arr: Post[]) =>
          arr.findIndex(p => p.id === post.id) === index
        );
        setPosts(uniquePosts);

        // Update cache with fresh posts
        updateCache({
          posts: uniquePosts,
          page: pageNum,
          hasMore: pagination.hasMore
        });
      }

      setHasMore(pagination.hasMore);
      setPage(pageNum);

    } catch (err) {
      // Error handled silently
      if (isMountedRef.current) {
        setError('Failed to load posts. Please try again.');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setLoadingMore(false);
        fetchInProgressRef.current = false;
      }
    }
  }, [updateCache, newPostsLoaded.length]);

  const loadMorePosts = useCallback(() => {
    if (!loadingMore && !loading && hasMore && !fetchInProgressRef.current) {
      fetchFeed(page + 1, true);
    }
  }, [loadingMore, loading, hasMore, page, fetchFeed]);

  // ==================== EVENT HANDLERS ====================
  const handleFeedTypeChange = (newFeedType: 'following' | 'foryou') => {
    if (newFeedType === 'following') {
      navigate('/following', { replace: false });
    }
    setShowDropdown(false);
  };

  const handlePostLiked = useCallback((postId: string, liked: boolean, actualCount: number) => {
    setPosts(prevPosts => {
      const updatedPosts = prevPosts.map(post =>
        post.id === postId
          ? { ...post, is_liked: liked, likes_count: actualCount }
          : post
      );

      // Update cache
      updateCache({ posts: updatedPosts });

      return updatedPosts;
    });
  }, [updateCache]);

  const handlePostDeleted = useCallback((postId: string) => {
    setPosts(prevPosts => {
      const updatedPosts = prevPosts.filter(post => post.id !== postId);

      // Update cache
      updateCache({ posts: updatedPosts });

      return updatedPosts;
    });
  }, [updateCache]);

  const handlePostEdited = useCallback((postId: string, newContent: string) => {
    setPosts(prevPosts => {
      const updatedPosts = prevPosts.map(post =>
        post.id === postId
          ? { ...post, post_content: newContent }
          : post
      );

      // Update cache
      updateCache({ posts: updatedPosts });

      return updatedPosts;
    });
  }, [updateCache]);

  // ==================== INITIALIZATION ====================
  // Set up mounted ref for cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ==================== SCROLL POSITION MANAGEMENT ====================
  // Save scroll position continuously while scrolling
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout;
    let lastSavedScrollY = 0;

    const handleScroll = () => {
      const scrollY = window.scrollY;

      // Debounce scroll saving to avoid too many writes
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (scrollY > 0) {
          sessionStorage.setItem(`scrollPos-${location.pathname}`, scrollY.toString());
          lastSavedScrollY = scrollY; // Keep track of the last saved position
        }
      }, 100);
    };

    const handleBeforeUnload = () => {
      const scrollY = window.scrollY;
      sessionStorage.setItem(`scrollPos-${location.pathname}`, scrollY.toString());
    };

    // Save current scroll position on mount
    const currentScrollY = window.scrollY;
    if (currentScrollY > 0) {
      sessionStorage.setItem(`scrollPos-${location.pathname}`, currentScrollY.toString());
      lastSavedScrollY = currentScrollY;
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleBeforeUnload);

      // Don't save on unmount if we have a better saved position
      const finalScrollY = window.scrollY;

      // Only save if the current position is better than what we last saved
      if (finalScrollY > lastSavedScrollY) {
        sessionStorage.setItem(`scrollPos-${location.pathname}`, finalScrollY.toString());
      }
    };
  }, [location.pathname]);

  // Manual scroll position save function
  const saveScrollPosition = useCallback(() => {
    const scrollY = window.scrollY;
    sessionStorage.setItem(`scrollPos-${location.pathname}`, scrollY.toString());
  }, [location.pathname]);

  // Restore scroll position on mount (only once, not when posts change)
  useEffect(() => {
    const storedScrollY = sessionStorage.getItem(`scrollPos-${location.pathname}`);
    const logoClickRefresh = sessionStorage.getItem('logoClickRefresh');

    // Clear the logo click refresh flag
    if (logoClickRefresh) {
      sessionStorage.removeItem('logoClickRefresh');
      return;
    }

    if (storedScrollY && posts.length > 0 && initialLoadComplete) {
      const scrollPosition = parseInt(storedScrollY, 10);
      // Use requestAnimationFrame to ensure DOM is painted
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition);
      });
    }
  }, [location.pathname, initialLoadComplete, posts.length]);

  // Add manual save on window focus/blur
  useEffect(() => {
    const handleBlur = () => {
      saveScrollPosition();
    };

    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('blur', handleBlur);
    };
  }, [saveScrollPosition]);

  // Save scroll position before navigation starts
  useEffect(() => {
    const handleBeforeNavigate = () => {
      const scrollY = window.scrollY;
      sessionStorage.setItem(`scrollPos-${location.pathname}`, scrollY.toString());
    };

    // Listen for navigation events
    window.addEventListener('beforeunload', handleBeforeNavigate);

    // Also save when the component is about to unmount due to route change
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      handleBeforeNavigate();
      return originalPushState.apply(this, args);
    };

    window.history.replaceState = function (...args) {
      handleBeforeNavigate();
      return originalReplaceState.apply(this, args);
    };

    return () => {
      window.removeEventListener('beforeunload', handleBeforeNavigate);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [location.pathname]);

  // Expose save function globally for debugging
  useEffect(() => {
    (window as any).saveForYouScrollPosition = saveScrollPosition;
    (window as any).getForYouScrollPosition = () => {
      const stored = sessionStorage.getItem(`scrollPos-${location.pathname}`);
      return stored;
    };

    return () => {
      delete (window as any).saveForYouScrollPosition;
      delete (window as any).getForYouScrollPosition;
    };
  }, [saveScrollPosition, location.pathname]);

  // Load current user
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem('user');
      if (savedUser) {
        setCurrentUser(JSON.parse(savedUser));
      }
    } catch (e) {
      // Error handled silently
    }
  }, []);


  // Main feed loading logic
  useEffect(() => {
    // Skip if already loaded
    if (initialLoadComplete) {
      return;
    }

    // Use cache if it exists
    if (feedCache.posts.length > 0) {
      // Ensure no duplicates in cached data
      const uniqueCachedPosts = feedCache.posts.filter((post: Post, index: number, arr: Post[]) =>
        arr.findIndex(p => p.id === post.id) === index
      );
      setPosts(uniqueCachedPosts);
      setPage(feedCache.page);
      setHasMore(feedCache.hasMore);
      setError(null);
      setInitialLoadComplete(true);
    } else if (!fetchInProgressRef.current) {
      // No cache exists - fetch fresh data and scroll to top
      // Clear saved scroll position since we're getting fresh content
      sessionStorage.removeItem(`scrollPos-${location.pathname}`);

      // Scroll to top for fresh content
      window.scrollTo(0, 0);

      fetchFeed(1, false, false);
      setInitialLoadComplete(true);
    }
  }, [initialLoadComplete, feedCache, fetchFeed, posts.length, location.pathname]);

  // ==================== SCROLL HANDLING ====================
  // Infinite scroll with debouncing
  useEffect(() => {
    let ticking = false;
    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
          const scrollHeight = document.documentElement.scrollHeight;
          const clientHeight = window.innerHeight;

          const reachedHalfway = scrollTop + clientHeight >= scrollHeight / 2;

          if (reachedHalfway && hasMore && !loadingMore && !loading && !fetchInProgressRef.current) {
            // Debounce to prevent multiple rapid calls
            if (scrollTimeout) {
              clearTimeout(scrollTimeout);
            }
            scrollTimeout = setTimeout(() => {
              if (hasMore && !loadingMore && !loading && !fetchInProgressRef.current) {
                loadMorePosts();
              }
            }, 150);
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [hasMore, loadingMore, loading, loadMorePosts]);





  // Listen for refresh feed event
  useEffect(() => {
    const handleRefreshFeed = () => {

      // Clear cache
      setFeedCache({
        posts: [],
        page: 1,
        hasMore: true,
        timestamp: 0
      });

      // Reset state
      setPosts([]);
      setPage(1);
      setHasMore(true);
      setError(null);
      setInitialLoadComplete(false);
      fetchInProgressRef.current = false;

      // Fetch fresh data
      fetchFeed(1, false, true);
    };

    window.addEventListener('refreshFeed', handleRefreshFeed);
    return () => window.removeEventListener('refreshFeed', handleRefreshFeed);
  }, [fetchFeed]);

  // Listen for profile picture updates
  useEffect(() => {
    const handleProfilePictureUpdate = (event: CustomEvent) => {
      if (!currentUser || !event.detail?.profile_picture_url) return;

      // Update current user
      setCurrentUser((prev: any) => ({
        ...prev,
        profile_picture_url: event.detail.profile_picture_url
      }));

      // Update posts
      setPosts(prevPosts => {
        const updatedPosts = prevPosts.map(post =>
          post.username === currentUser.username
            ? { ...post, profile_picture_url: event.detail.profile_picture_url }
            : post
        );

        // Update cache
        updateCache({ posts: updatedPosts });

        return updatedPosts;
      });
    };

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate as EventListener);
    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate as EventListener);
    };
  }, [currentUser, updateCache]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  // ==================== RENDER HELPERS ====================
  const renderFeedToggle = () => (
    <>
      {/* Desktop Dropdown */}
      <div className="feed-dropdown-container" ref={dropdownRef}>
        <button
          className="feed-dropdown-button"
          onClick={() => setShowDropdown(!showDropdown)}
          aria-label="Select feed type"
        >
          <span className="feed-dropdown-text">For You</span>
          <ChevronDown className={`feed-dropdown-icon ${showDropdown ? 'open' : ''}`} />
        </button>

        {showDropdown && (
          <div className="feed-dropdown-menu">
            <button
              className="feed-dropdown-item active"
              onClick={() => handleFeedTypeChange('foryou')}
            >
              <span>For You</span>
              <Check className="feed-dropdown-check" />
            </button>
            <button
              className="feed-dropdown-item"
              onClick={() => handleFeedTypeChange('following')}
            >
              <span>Following</span>
            </button>
          </div>
        )}
      </div>

      {/* Mobile Toggle */}
      <div className="feed-toggle-container">
        <button
          className="feed-toggle-item active"
          onClick={() => handleFeedTypeChange('foryou')}
        >
          For You
        </button>
        <button
          className="feed-toggle-item"
          onClick={() => handleFeedTypeChange('following')}
        >
          Following
        </button>
      </div>
    </>
  );

  // ==================== SKELETON ====================
  const renderSkeletonPosts = () => {
    return Array.from({ length: 5 }, (_, index) => (
      <div key={index} className="feed-skeleton-post">
        <div className="feed-skeleton-post-header">
          <div className="feed-skeleton-post-avatar"></div>
          <div className="feed-skeleton-post-info">
            <div className="feed-skeleton-post-username"></div>
            <div className="feed-skeleton-post-time"></div>
          </div>
        </div>
        <div className="feed-skeleton-post-content">
          <div className="feed-skeleton-post-line"></div>
          <div className="feed-skeleton-post-line short"></div>
        </div>
      </div>
    ));
  };

  // ==================== RENDER ====================
  // Show skeleton while loading initial data or restoring scroll
  const showSkeleton = loading && posts.length === 0;
  if (showSkeleton) {
    return (
      <>
        <MetaTags
          title="For You - Mindcast"
          description="The Playground for Curious Minds"
          canonicalUrl="https://mindcast.app/feed"
        />
        <div className="feed-page-container">
          {renderFeedToggle()}
          <div className="feed-posts-list" ref={feedPostsListRef}>
            <div className="feed-posts-list-top-border"></div>
            {renderSkeletonPosts()}
          </div>
        </div>
      </>
    );
  }

  // Show error state
  if (error && posts.length === 0 && !loading) {
    return (
      <>
        <MetaTags
          title="For You - Mindcast"
          description="The Playground for Curious Minds"
          canonicalUrl="https://mindcast.app/feed"
        />

        <div className="feed-page-container">
          {renderFeedToggle()}
          <div className="feed-error-container">
            <div className="feed-posts-list-top-border"></div>
            <div className="feed-error-message">
              <h3>Something went wrong</h3>
              <p>{error}</p>
              <button onClick={() => fetchFeed(1, false, true)} className="retry-button">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Main render
  return (
    <>
      <MetaTags
        title="For You - Mindcast"
        description="The Playground for Curious Minds"
        canonicalUrl="https://mindcast.app/feed"
      />
      <div className="feed-page-container">
        {renderFeedToggle()}

        <div className="feed-posts-list" ref={feedPostsListRef}>
          <div className="feed-posts-list-top-border"></div>
          {posts.length === 0 ? (
            <div className="feed-empty-feed">
              <p>No posts yet. Be the first to share something!</p>
            </div>
          ) : (
            <>
              {posts.map((post) => (
                <div
                  key={post.id}
                  className={`feed-post-wrapper ${newPostsLoaded.includes(post.id) ? 'feed-new-post-animation' : ''}`}
                >
                  <PostCard
                    post={post}
                    onLikeToggle={handlePostLiked}
                    onDelete={handlePostDeleted}
                    onEdit={handlePostEdited}
                    feedType="foryou"
                    feedPostsListRef={feedPostsListRef}
                  />
                </div>
              ))}

              {loadingMore && (
                <div className="feed-loading-more-container">
                  <div className="feed-loading-spinner"></div>
                  <span>Loading more posts...</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ForYou;